import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { flows, billing } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Card, Group, Button, Text, ActionIcon } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea, Switch, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useCredits } from '../../lib/credits';
import { useT } from '../../i18n/I18nProvider';
import { useBots, botName } from '../../lib/bots';
import { flowErrorMessage } from '../../lib/flow-limit';
import { formatDateTime, formatRelative, formatCount, type Indexed } from '../../lib/format';
import {
  Banner, Filter, SearchField, StatusPill, RowName,
  FilterIcon, PlusIcon
} from '../../components/panel';
import { hasTrigger, unlinkedNodeIds } from './builder/graph-ops';
import { flowToGraph } from './builder/mapper';
import { flowAskedFor } from './builder/components/catalog';
import { LooseEndsBar } from './components/LooseEndsBar';
import { DeleteFlowDialog, confirmSwitchOff } from './components/FlowGuards';
import './flows.css';

// A flow is a graph the engine walks per conversation: a trigger, nodes, and
// the edges between them. This starter greets, asks a question, waits for the
// answer, and branches on it: the shape most chatbots begin from. Its copy is
// translated, so an Afrikaans tenant starts with an Afrikaans greeting.
function defaultDefinition(t: (key: string) => string): string {
  return JSON.stringify(
    {
      version: 2,
      trigger: { type: 'keyword', keyword: t('flows.starter.keyword') },
      nodes: [
        { id: 'n1', type: 'send', name: t('flows.starter.greetingName'), body: t('flows.starter.greeting') },
        {
          id: 'n2', type: 'ask', name: t('flows.starter.askName'), question: t('flows.starter.question'), variable: 'need',
          options: [{ id: 'order', title: t('flows.starter.optionOrder') }, { id: 'person', title: t('flows.starter.optionPerson') }]
        },
        { id: 'n3', type: 'send', name: t('flows.starter.orderName'), body: t('flows.starter.orderHelp') },
        { id: 'n4', type: 'handoff', name: t('flows.starter.handoffName'), always: true, message: t('flows.starter.handoff') }
      ],
      edges: [
        { from: 'n1', to: 'n2' },
        { from: 'n2', to: 'n3', branch: 'order' },
        { from: 'n2', to: 'n4', branch: 'person' }
      ]
    },
    null,
    2
  );
}

export default function FlowsPage() {
  const nav = useNavigate();
  const t = useT();
  const credits = useCredits();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<flows.Flow | 'new' | null>(null);
  const [cloneFrom, setCloneFrom] = useState<flows.Flow | null>(null);
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 250);
  const [onlyActive, setOnlyActive] = useState<'' | 'active' | 'paused'>('');

  const { data, isPending, error } = useQuery({
    queryKey: ['flows'],
    queryFn: () => flows.listFlows()
  });

  /*
   * The plan's flows (30 October 2026): "4 of 10 flows used", and that entry points do not count. Nothing on a plan
   * with no limit, where the count means nothing. The Flows half of ENGINEERING-BACKLOG's "x of y used" item.
   */
  const allowance = data?.meta?.allowance;
  const entryFree = data?.meta?.entry_free ?? 0;
  const limited = allowance && !allowance.unlimited && allowance.allowance != null ? allowance.allowance : null;
  const allowanceLine = limited == null ? null : [
    t('flows.allowance.used', { current: formatCount(allowance!.current), allowance: formatCount(limited) }),
    t('flows.allowance.entryFree'),
    entryFree > 0 ? t('flows.allowance.entryFreeCount', { count: formatCount(entryFree) }) : ''
  ].filter(Boolean).join(' ');
  const full = limited != null && allowance!.current >= limited;

  // One chatbot's flows at a time; every flow for an organisation with one.
  const { bots, multi, active, setActive, owns } = useBots();
  const all = useMemo(() => ((data?.data ?? []) as Indexed<flows.Flow>[]).filter(owns), [data, owns]);

  // §4 item 9: `listFlows()` returns everything, so with thirty flows the page
  // was a wall with no search, filter or counts.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((f) => {
      if (onlyActive === 'active' && !f.is_active) return false;
      if (onlyActive === 'paused' && f.is_active) return false;
      if (!q) return true;
      return f.name.toLowerCase().includes(q) || (f.description ?? '').toLowerCase().includes(q);
    });
  }, [all, search, onlyActive]);

  // Deleting names the switched-on flows that open it first, and can point their steps elsewhere (2 November 2026).
  const [deleting, setDeleting] = useState<flows.Flow | null>(null);

  const toggleActive = useMutation({
    // §4 item 2: neither mutation had success copy or an error handler, and
    // activating a flow starts answering real customers automatically.
    // §4 item 13: this re-sent `name` and `definition` although the request is
    // fully partial, so pausing a flow clobbered a concurrent edit.
    mutationFn: (f: flows.Flow) => flows.updateFlow(f.id, { is_active: !f.is_active }),
    onSuccess: (_r, f) => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(
        f.is_active ? t('flows.toast.paused', { name: f.name }) : t('flows.toast.activated', { name: f.name }),
        t('flows.title')
      );
    },
    onError: (e) => notifyError(flowErrorMessage(e, t), t('flows.toast.toggleFailed'))
  });

  /**
   * Activating starts replying to real people; that deserves saying out loud. Switching off asks first only when
   * switched-on flows open this one, and names them (2 November 2026).
   */
  function confirmToggle(f: flows.Flow) {
    if (f.is_active) { void confirmSwitchOff(f, t, () => toggleActive.mutate(f)); return; }
    openConfirmModal({
      title: t('flows.modal.activate.title', { name: f.name }),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('flows.modal.activate.body')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.modal.activate.hint')}</Text>
        </Stack>
      ),
      labels: { confirm: t('action.activate'), cancel: t('action.cancel') },
      onConfirm: () => toggleActive.mutate(f)
    });
  }

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [creatingWithAi, setCreatingWithAi] = useState(false);
  const activeFilterCount = (search.trim() ? 1 : 0) + (onlyActive ? 1 : 0);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('flows.title')}
        description={t('flows.description')}
        withDivider={false}
      />

      {/* What a flow IS, said once at the top. Nothing on the page explained
          that these run on their own, which is the one thing a person needs to
          know before they switch one on. */}
      <div className="rr-flows__intro">
        <Banner tone="info">{t('flows.banner')}</Banner>
        <Group gap="xs">
          <Button
            size="sm"
            leftSection={<PlusIcon />}
            onClick={() => { setCloneFrom(null); setEditing('new'); }}
          >
            {t('flows.actions.create')}
          </Button>
          {/* No canvas to arrange: say what the bot should do and the assistant builds it. */}
          <Button
            size="sm"
            variant="default"
            loading={creatingWithAi}
            onClick={async () => {
              setCreatingWithAi(true);
              try {
                const res = await flows.createFlow({
                  name: t('flows.assistant.newFlowName'),
                  definition: { version: 2, trigger: { type: 'always' }, nodes: [], edges: [] },
                  is_active: false,
                  bot_id: active?.id
                });
                nav(`/flows/${res.data.id}/builder?assistant=1`);
              } catch (e) {
                notifyError(flowErrorMessage(e, t), t('flows.title'));
              } finally {
                setCreatingWithAi(false);
              }
            }}
          >
            {t('flows.actions.createWithAi')}
          </Button>
          {/* Suggest flows with AI (30 October 2026): suggestions from ideas and the knowledge base, only the picked ones built. */}
          <Button size="sm" variant="default" onClick={() => nav('/flows/ideas')} data-testid="flows-suggest-ai-list">
            {t('flows.actions.suggestWithAi')}
          </Button>
        </Group>
      </div>

      {allowanceLine && (
        <Text role="caption" c="var(--rr-color-text-muted)" data-testid="flows-allowance">
          {allowanceLine}
          {full && <>{' '}<Link to="/billing">{t('flows.allowance.seePlans')}</Link></>}
        </Text>
      )}

      {/* Steps in switched-on flows that lead nowhere, with Review (2 November 2026). */}
      <LooseEndsBar />

      {error && <Alert color="error" role="alert">{t('flows.loadError')}</Alert>}

      <Card withBorder noPadding className="rr-panel">
        <div className="rr-panel__filters">
          <Filter label={t('flows.search.label')}>
            <SearchField>
              <TextInput
                type="search"
                className="rr-panel__search-input"
                placeholder={t('flows.search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
            </SearchField>
          </Filter>

          {active && (
            <Filter label={t('bots.switcher.label')}>
              <Select
                value={active.id}
                onChange={(e) => setActive(e.currentTarget.value)}
                data={bots.map((b) => ({ value: b.id, label: botName(b, t('settings.assistant.defaultName')) }))}
              />
            </Filter>
          )}

          <Filter label={t('common.status')}>
            <Select
              value={onlyActive}
              onChange={(e) => setOnlyActive(e.currentTarget.value as '' | 'active' | 'paused')}
              data={[
                { value: '', label: t('flows.filters.status.any', { count: all.length }) },
                { value: 'active', label: `${t('status.active')} (${all.filter((f) => f.is_active).length})` },
                { value: 'paused', label: `${t('status.paused')} (${all.filter((f) => !f.is_active).length})` }
              ]}
            />
          </Filter>

          <div className="rr-panel__filter-actions">
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="rr-panel__clear"
                onClick={() => { setQuery(''); setOnlyActive(''); }}
              >
                {t('contacts.filters.clear')}
              </button>
            )}
            <button
              type="button"
              className={`rr-panel__filterbtn${activeFilterCount > 0 ? ' is-active' : ''}`}
              aria-pressed={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <FilterIcon />
              {t('contacts.filters.title')}
              {activeFilterCount > 0 && <span className="rr-panel__filterbtn-count">{activeFilterCount}</span>}
            </button>
          </div>
        </div>

        {!isPending && rows.length === 0 ? (
          <div style={{ padding: '24px 18px' }}>
            <EmptyState
              title={all.length === 0 ? t('flows.empty') : t('flows.emptyFiltered')}
              description={all.length === 0 ? t('flows.empty.hint') : undefined}
              action={
                all.length === 0
                  ? <Button size="xs" onClick={() => { setCloneFrom(null); setEditing('new'); }}>{t('flows.actions.create')}</Button>
                  : <Button size="xs" variant="default" onClick={() => { setOnlyActive(''); setQuery(''); }}>{t('contacts.filters.clear')}</Button>
              }
            />
          </div>
        ) : (
          <DataTable<Indexed<flows.Flow>>
            records={rows}
            loading={isPending}
            idAccessor="id"
            onRowClick={(f) => nav(`/flows/${f.id}`)}
            emptyState={t('flows.empty')}
            columns={[
              {
                accessor: 'name',
                title: t('common.name'),
                render: (f) => (
                  <RowName
                    icon={<FlowIcon />}
                    tint={f.is_active ? 'var(--rr-color-success, #16a34a)' : 'var(--rr-color-text-muted)'}
                    title={f.name}
                    sub={flowAskedFor(f.definition) ? t(`flows.intentBadge.${flowAskedFor(f.definition)}`) : undefined}
                  />
                )
              },
              {
                accessor: 'description',
                title: t('common.description'),
                // §4 item 1: there was no column behind this, so it rendered
                // "–" for every row and what the user typed was discarded.
                // `flows.description` exists now (migration 003).
                render: (f) => f.description
                  ? <span className="rr-flows__desc">{f.description}</span>
                  : <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>
              },
              {
                accessor: 'is_active',
                title: t('common.status'),
                render: (f) => (
                  <StatusPill tone={f.is_active ? 'active' : 'paused'}>
                    {f.is_active ? t('status.active') : t('status.paused')}
                  </StatusPill>
                )
              },
              {
                accessor: 'spend',
                title: t('flows.columns.spend'),
                textAlign: 'right',
                // Model tokens, WhatsApp replies and notify-step sends, from
                // the ledger, attributed to the flow.
                render: (f) => <FlowSpend flowId={f.id} />
              },
              {
                accessor: 'health',
                title: t('flows.columns.health'),
                // §4 item 6: an active flow erroring for a week looked
                // identical to a healthy one.
                render: (f) => <FlowHealth flowId={f.id} />
              },
              { accessor: 'updated_at', title: t('common.updated'), render: (f) => formatDateTime(f.updated_at) },
              {
                accessor: 'actions',
                title: t('common.actions'),
                render: (f) => (
                  <div className="rr-rowactions" onClick={(e) => e.stopPropagation()}>
                    {/* The three things people do to a flow, one click each,
                        rather than all of them behind a menu. */}
                    <Button
                      size="xs"
                      variant="default"
                      leftSection={<RunsIcon />}
                      onClick={() => nav(`/flows/${f.id}?tab=runs`)}
                    >
                      {t('flows.view.tabs.runs')}
                    </Button>
                    <Tooltip label={t('flows.builder.open')} position="top">
                      <ActionIcon
                        variant="default"
                        size="sm"
                        aria-label={t('flows.builder.open')}
                        onClick={() => nav(`/flows/${f.id}/builder`)}
                      >
                        <PencilIcon />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={f.is_active ? t('action.pause') : t('action.activate')} position="top">
                      <ActionIcon
                        variant="default"
                        size="sm"
                        aria-label={f.is_active ? t('action.pause') : t('action.activate')}
                        onClick={() => confirmToggle(f)}
                      >
                        {f.is_active ? <PauseIcon /> : <PlayIcon />}
                      </ActionIcon>
                    </Tooltip>

                    <Menu>
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm" aria-label={t('flows.actions.for', { name: f.name })}>
                          <MoreIcon />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => nav(`/flows/${f.id}`)}>{t('action.open')}</Menu.Item>
                        <Menu.Item onClick={() => { setCloneFrom(null); setEditing(f); }}>{t('flows.actions.editJson')}</Menu.Item>
                        <Menu.Item onClick={() => { setCloneFrom(f); setEditing('new'); }}>{t('templates.actions.duplicate')}</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={() => setDeleting(f)}>
                          {t('action.delete')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                )
              }
            ]}
          />
        )}

        {rows.length > 0 && (
          <div className="rr-panel__foot">
            <span>{t('flows.showingOf', { count: formatCount(rows.length), total: formatCount(all.length) })}</span>
          </div>
        )}
      </Card>

      <DeleteFlowDialog flow={deleting} onClose={() => setDeleting(null)} />

      <FlowDrawer
        flow={editing}
        cloneFrom={cloneFrom}
        onClose={() => { setEditing(null); setCloneFrom(null); }}
        onSaved={() => qc.invalidateQueries({ queryKey: ['flows'] })}
      />
    </Stack>
  );
}

function FlowIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="5" cy="5" r="2.3" />
      <circle cx="15" cy="10" r="2.3" />
      <circle cx="5" cy="15" r="2.3" />
      <path d="M7.3 5h2.9a2.5 2.5 0 0 1 2.5 2.5v.2M7.3 15h2.9a2.5 2.5 0 0 0 2.5-2.5v-.2" />
    </svg>
  );
}
function RunsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h7" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11.2 2.3 13.7 4.8 5.5 13H3v-2.5z" />
      <path d="M9.8 3.7 12.3 6.2" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <rect x="4" y="3" width="3" height="10" rx="1" />
      <rect x="9" y="3" width="3" height="10" rx="1" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M5 3.4v9.2a.6.6 0 0 0 .92.5l7-4.6a.6.6 0 0 0 0-1l-7-4.6A.6.6 0 0 0 5 3.4z" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="8" cy="3.2" r="1.35" />
      <circle cx="8" cy="8" r="1.35" />
      <circle cx="8" cy="12.8" r="1.35" />
    </svg>
  );
}

/** What the flow has cost this month, from the attributed ledger. */
function FlowSpend({ flowId }: { flowId: string }) {
  const t = useT();
  const credits = useCredits();
  const { data } = useQuery({
    queryKey: ['usage', 'sources', 'flow', flowId],
    queryFn: () => billing.getUsageSources('flow', [flowId]),
    staleTime: 60_000,
    retry: false
  });
  if (!data) return <Text role="caption" c="var(--rr-color-text-muted)">…</Text>;
  const row = data.data[0];
  return row
    ? <Text role="bodySmall" fw={600} title={t('flows.spend.hint', { count: row.entries })}>{credits(row.amount_minor, row.currency)}</Text>
    : <Text role="caption" c="var(--rr-color-text-muted)">{credits(0, 'ZAR')}</Text>;
}

/**
 * Last run and recent failures.
 *
 * §4 item 6: nothing on this page said whether a flow was working.
 * `listFlowRuns` already returns the statuses and a total, so this is one
 * cheap call per row, cached for a minute.
 */
function FlowHealth({ flowId }: { flowId: string }) {
  const t = useT();
  const credits = useCredits();
  const { data } = useQuery({
    queryKey: ['flow-runs', flowId, 'health'],
    queryFn: () => flows.listFlowRuns(flowId, { limit: 20 }),
    staleTime: 60_000
  });

  const runs = data?.data ?? [];
  if (!data) return <Text role="caption" c="var(--rr-color-text-muted)">…</Text>;
  if (runs.length === 0) return <Text role="caption" c="var(--rr-color-text-muted)">{t('flows.health.neverRun')}</Text>;

  const failed = runs.filter((r) => r.status === 'failed').length;
  const latest = runs[0];

  return (
    <Tooltip
      label={failed > 0
        ? t('flows.health.failedTooltip', { failed, total: runs.length })
        : t('flows.health.okTooltip', { total: runs.length })}
      position="top"
    >
      <Group gap={6} wrap="nowrap">
        <span className={`rr-flows__health-dot${failed > 0 ? ' is-failing' : ''}`} aria-hidden />
        <Text role="caption" c="var(--rr-color-text-muted)">
          {latest.started_at ? formatRelative(latest.started_at) : t('flows.health.neverRun')}
        </Text>
        <span className="rr-visually-hidden">
          {failed > 0 ? t('flows.health.failedTooltip', { failed, total: runs.length }) : t('flows.health.okTooltip', { total: runs.length })}
        </span>
      </Group>
    </Tooltip>
  );
}

export function FlowDrawer({
  flow,
  cloneFrom,
  onClose,
  onSaved
}: {
  flow: flows.Flow | 'new' | null;
  cloneFrom: flows.Flow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const credits = useCredits();
  const nav = useNavigate();
  const isNew = flow === 'new';
  const existing = isNew || flow === null ? null : flow;
  const open = flow !== null;
  // A new flow belongs to the chatbot being looked at; an existing one can be
  // moved. Neither shows for an organisation with a single chatbot.
  const { bots, multi, active } = useBots();
  const [botId, setBotId] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [definition, setDefinition] = useState(() => defaultDefinition(t));
  const [isActive, setIsActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState('');

  const target = existing?.id ?? (isNew ? `new-${cloneFrom?.id ?? ''}` : null);
  useEffect(() => {
    if (!open) return;
    const source = existing ?? cloneFrom;
    setName(existing?.name ?? (cloneFrom ? `${cloneFrom.name} (copy)` : ''));
    setDescription(source?.description ?? '');
    setDefinition(source ? JSON.stringify(source.definition, null, 2) : defaultDefinition(t));
    // A clone always starts paused: it is a draft of something, not a live
    // change to how customers are answered.
    setIsActive(existing?.is_active ?? false);
    setBotId(existing?.bot_id ?? active?.id ?? '');
    setNameError('');
  }, [open, target]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * §4 item 8: a flow saved with no trigger never fires and the drawer said
   * nothing, though the builder shows an Alert for exactly this and
   * `graph-ops` exports the pure checks.
   */
  const problems = useMemo(() => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(definition);
    } catch {
      return ['flows.validate.invalidJson'];
    }

    // The drawer edits the ENGINE definition; `hasTrigger` and
    // `unlinkedNodeIds` operate on the BUILDER graph, so the definition is
    // mapped through `flowToGraph` first rather than being passed to them
    // raw: the two shapes are not the same.
    const graph = flowToGraph(parsed);
    const found: string[] = [];
    if (!hasTrigger(graph)) found.push('flows.validate.noTrigger');
    if (unlinkedNodeIds(graph).length > 0) found.push('flows.validate.unlinked');
    return found;
  }, [definition]);

  async function save() {
    if (!name.trim()) { setNameError(t('flows.drawer.nameRequired')); return; }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(definition);
    } catch {
      return;
    }
    // Switching a live flow off here names the switched-on flows that open it first (2 November 2026).
    if (existing?.is_active && !isActive) {
      setBusy(true);
      await confirmSwitchOff(existing, t, () => void write(parsed));
      setBusy(false);
      return;
    }
    await write(parsed);
  }

  async function write(parsed: Record<string, unknown>) {
    setBusy(true);
    try {
      if (isNew) {
        await flows.createFlow({ name: name.trim(), description: description || undefined, definition: parsed, is_active: isActive, bot_id: botId || active?.id });
      } else if (existing) {
        await flows.updateFlow(existing.id, {
          name: name.trim(), description, definition: parsed, is_active: isActive,
          bot_id: botId && botId !== existing.bot_id ? botId : undefined
        });
      }
      notifySuccess(t('flows.toast.saved', { name }), t('flows.title'));
      onSaved();
      onClose();
    } catch (e) {
      // A plan with no room left says so in its own words, rather than the service's English.
      notifyError(flowErrorMessage(e, t), t('flows.builder.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={isNew ? t('flows.drawer.new') : t('flows.drawer.edit')}
      position="right"
      size="lg"
    >
      <Stack gap="md" style={{ padding: 4 }}>
        <TextInput
          label={t('common.name')}
          error={nameError}
          value={name}
          onChange={(e) => { setName(e.currentTarget.value); setNameError(''); }}
          required
        />
        {multi && (
          <Select
            label={t('bots.flow.label')}
            description={t('bots.flow.hint')}
            value={botId}
            onChange={(e) => setBotId(e.currentTarget.value)}
            data={bots.map((b) => ({ value: b.id, label: botName(b, t('settings.assistant.defaultName')) }))}
          />
        )}
        <TextInput
          label={t('common.description')}
          description={t('flows.drawer.descriptionHint')}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Stack gap={4}>
          <Group justify="space-between" align="center">
            <Text role="inputLabel">{t('flows.drawer.definition')}</Text>
            {existing && (
              <Button size="xs" variant="subtle" onClick={() => nav(`/flows/${existing.id}/builder`)}>
                {t('flows.builder.open')}
              </Button>
            )}
          </Group>
          <Text role="caption" c="var(--rr-color-text-muted)">
            {/* Shape hints are code, not copy: one key, schema fragments inlined. */}
            {t('flows.drawer.definitionHint')}
          </Text>
          <Textarea
            value={definition}
            onChange={(e) => setDefinition(e.currentTarget.value)}
            minRows={14}
            className="rr-flows__code"
            aria-label={t('flows.drawer.definition')}
          />
        </Stack>

        {problems.length > 0 && (
          <Alert color="warning" role="alert">
            <Stack gap={4}>
              {problems.map((key) => <Text key={key} role="caption">· {t(key)}</Text>)}
            </Stack>
          </Alert>
        )}

        <Switch label={t('status.active')} description={t('flows.drawer.activeHint')} checked={isActive} onChange={(next) => setIsActive(next)} />
        <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={save} loading={busy} disabled={problems.includes('flows.validate.invalidJson')}>
            {t('action.save')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
